import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import hammer from "@/assets/images/hammer.png";
import { CONSTANTS } from "@/constants";
import { useWalletAddress } from "@/hooks/useWalletAddress";
import { useChestNumber } from "../chest-number/data";
import { toaster } from "../toaster";
import { useClaimedDaily } from "./event-free-data";
import { request } from "@/utils/request";

const CHECK_EVENT = "event-free-smash";

const getLocalData = () => {
  const data = localStorage.getItem(CHECK_EVENT);
  if (!data) {
    return {};
  }
  const val = JSON.parse(data);
  const timeCheck = new Date().toISOString().split("T")[0];
  if (val?.time === timeCheck) {
    return val?.data || {};
  }
  return {};
};

export function EventFreeSmash() {
  const { t } = useTranslation();
  const address = useWalletAddress();
  const { data: checkDaily, isLoading, mutate } = useClaimedDaily(address);
  const { mutate: refreshChest } = useChestNumber(address);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loadingClaim, setLoadingClaim] = useState<boolean>(false);
  const [checkData, setCheckData] = useState<{
    aion?: boolean;
    winance?: boolean;
    dalgona?: boolean;
  }>(getLocalData());

  const disabled = useMemo(() => {
    return !(checkData?.aion && checkData?.dalgona && checkData?.winance);
  }, [checkData]);

  const data = [
    {
      img: require("@/assets/events/aion.png"),
      txt: `${t("Open")} AION`,
      key: "aion",
      href: `https://world.org/mini-app?app_id=app_cb736f87fc78c84c31201f140bcfb5c0`,
    },
    {
      img: require("@/assets/events/winance.png"),
      txt: `${t("Open")} Winance`,
      key: "winance",
      href: `https://world.org/mini-app?app_id=app_60c094705c114bff41be480e736d9482`,
    },
    {
      img: require("@/assets/events/dalgona.png"),
      txt: `${t("Open")} Dalgona`,
      key: "dalgona",
      href: `https://world.org/mini-app?app_id=app_87ae73f8d0558b142fb9c5fc7811b8af`,
    },
  ];

  const handleSetData = useCallback((key: string) => {
    setCheckData((e) => {
      const data = { ...e, [key]: true };
      localStorage.setItem(
        CHECK_EVENT,
        JSON.stringify({
          time: new Date().toISOString().split("T")[0],
          data: data,
        })
      );
      return data;
    });
  }, []);

  const handleClaim = useCallback(() => {
    if (disabled) {
      return;
    }
    setLoadingClaim(true);
    request(
      `${CONSTANTS.BASE_URL}/api/world-coin-chest-smasher/extra-eggs/daily-generate?wallet=${address}`,
      { method: "POST" }
    )
      .then((res: any) => {
        return res.json();
      })
      .then((e: any) => {
        if (!e.success) {
          throw new Error(e.message);
        }
        mutate();
        refreshChest();
        toaster.success(t("Claim Successfully"));
        setIsOpen(false);
      })
      .catch((e: any) => {
        toaster.error(e?.message || "You already claimed reward");
      })
      .finally(() => {
        setLoadingClaim(false);
      });
  }, [address, disabled, mutate, refreshChest, t]);

  if (isLoading || !checkDaily || !!checkDaily.data) {
    return null;
  }

  return (
    <>
      <div
        className="w-full px-3 py-4 flex flex-row gap-4 rounded-3xl items-center justify-between cursor-pointer"
        onClick={() => {
          setIsOpen(true);
          setLoadingClaim(false);
        }}
        style={{
          backgroundImage: `url(${require("@/assets/events/bg_star.png")})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "cover",
        }}
      >
        <div className="flex flex-row gap-2 px-2 items-center">
          <img src={hammer} className="w-6 h-6" alt="hammer" />

          <p className="text-[#0E0907] text-base font-semibold leading-5">
            {t("Earn Extra Free Smash")}
          </p>
        </div>
        <div className="px-2.5 py-1 bg-white text-black text-base font-semibold leading-6 rounded-full">
          {t("Earn")}
        </div>
      </div>
      <Sheet
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        detent="content"
      >
        <Sheet.Container className="!bg-[#1C150F] !rounded-t-2xl">
          <Sheet.Content className="bg-[#1C150F] !rounded-t-2xl">
            <div className="flex flex-col pb-4">
              <div className="flex flex-row gap-4 p-4 items-center justify-between">
                <p className="text-white text-base font-semibold leading-5">
                  {t("Earn Extra Free Smash")}
                </p>

                <button
                  className="w-5 h-5"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_576_5721)">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.99993 11.1783L14.7141 15.8925C14.8713 16.0443 15.0818 16.1283 15.3003 16.1264C15.5188 16.1245 15.7278 16.0368 15.8823 15.8823C16.0368 15.7278 16.1244 15.5188 16.1263 15.3003C16.1282 15.0818 16.0442 14.8713 15.8924 14.7141L11.1783 9.99996L15.8924 5.2858C16.0442 5.12863 16.1282 4.91812 16.1263 4.69963C16.1244 4.48113 16.0368 4.27212 15.8823 4.11761C15.7278 3.96311 15.5188 3.87547 15.3003 3.87357C15.0818 3.87167 14.8713 3.95566 14.7141 4.10746L9.99993 8.82163L5.28577 4.10746C5.12789 3.95942 4.91861 3.8786 4.70221 3.88211C4.48581 3.88563 4.27926 3.9732 4.12628 4.12629C3.97329 4.27938 3.88587 4.48599 3.88251 4.7024C3.87915 4.9188 3.96011 5.12802 4.10827 5.2858L8.8216 9.99996L4.10743 14.7141C4.02784 14.791 3.96436 14.883 3.92068 14.9846C3.87701 15.0863 3.85402 15.1956 3.85306 15.3063C3.8521 15.4169 3.87318 15.5267 3.91508 15.6291C3.95698 15.7315 4.01886 15.8245 4.0971 15.9028C4.17535 15.981 4.26839 16.0429 4.37081 16.0848C4.47322 16.1267 4.58295 16.1478 4.6936 16.1468C4.80425 16.1459 4.9136 16.1229 5.01527 16.0792C5.11694 16.0355 5.20889 15.9721 5.28577 15.8925L9.99993 11.1783Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_576_5721">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
              <div className="flex flex-col px-4">
                {data.map((e, index) => (
                  <div key={e.txt} className="flex flex-col">
                    {index !== 0 ? (
                      <div className="py-2">
                        <div className="border-t border-[#5F3A16] w-full"></div>
                      </div>
                    ) : null}
                    <div className="flex flex-row items-center justify-between py-3">
                      <div className="flex flex-row items-center gap-2">
                        <img src={e.img} className="w-8 h-8" alt={e.txt} />
                        <p className="text-sm text-white font-medium">
                          {e.txt}
                        </p>
                      </div>

                      <ButtonOpen
                        onClick={() => {
                          handleSetData(e.key);
                        }}
                        href={e.href}
                        check={(checkData as any)[e.key]}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-[#24201C]">
                <div
                  className={clsx(
                    "rounded-full p-3 text-center flex items-center justify-center cursor-pointer",
                    {
                      "opacity-30": disabled,
                    }
                  )}
                  style={{
                    background: "linear-gradient(90deg, #FEC537, #F68703)",
                  }}
                  onClick={() => {
                    if (disabled) {
                      return;
                    }
                    handleClaim();
                  }}
                >
                  {loadingClaim ? (
                    <SpinnerLoading />
                  ) : (
                    <p className="text-black text-base font-semibold uppercase">
                      {t("Claim now")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop
          className="bg-black/70"
          onTap={() => {
            setIsOpen(false);
          }}
        />
      </Sheet>
    </>
  );
}

function ButtonOpen(props: {
  onClick: () => void;
  check: boolean;
  href: string;
}) {
  const { onClick, check, href } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => {
        setIsLoading(true);
        setTimeout(() => {
          onClick();
          setIsLoading(false);
        }, 2000);
        window.open(href, "_blank");
      }}
      style={{
        minWidth: 90,
        background:
          isLoading || check
            ? "linear-gradient(90deg, #3C2B00, #422400)"
            : "linear-gradient(90deg, #FEC537, #F68703)",
      }}
      className="px-6 py-2 rounded-full flex items-center justify-center"
    >
      {check ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="17"
          viewBox="0 0 21 17"
          fill="none"
          className="w-6 h-6"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.9949 0.133082C18.2165 -0.000209019 18.4811 -0.0424837 18.7332 0.0151302C18.9853 0.0727441 19.2052 0.225767 19.3469 0.442082L20.3369 1.95208C20.466 2.14934 20.5212 2.38585 20.4928 2.61987C20.4645 2.85388 20.3544 3.07036 20.1819 3.23108L20.1789 3.23508L20.1649 3.24808L20.1079 3.30108L19.8829 3.51608C18.6379 4.72368 17.4307 5.96961 16.2629 7.25208C14.0659 9.66808 11.4569 12.8301 9.70091 15.8981C9.21091 16.7541 8.01391 16.9381 7.30391 16.1991L0.818913 9.46108C0.725978 9.36448 0.653413 9.25019 0.60553 9.12499C0.557646 8.99979 0.535423 8.86624 0.540181 8.73228C0.544939 8.59832 0.57658 8.46668 0.633224 8.34519C0.689869 8.2237 0.77036 8.11484 0.869913 8.02508L2.82991 6.25708C3.00215 6.10179 3.22301 6.01119 3.45469 6.0008C3.68637 5.9904 3.91445 6.06085 4.09991 6.20008L7.40891 8.68108C12.5779 3.58408 15.5089 1.62808 17.9949 0.133082Z"
            fill="#DF7F01"
          />
        </svg>
      ) : isLoading ? (
        <SpinnerLoading />
      ) : (
        <span className="text-black text-base font-semibold leading-6">
          {t("Open")}
        </span>
      )}
    </button>
  );
}

function SpinnerLoading() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={25}
      height={24}
      viewBox="0 0 25 24"
      fill="none"
      className="animate-spin"
    >
      <g clipPath="url(#clip0_576_11162)">
        <path
          d="M11.386 2.061C11.6496 2.03183 11.914 2.10858 12.1211 2.27436C12.3281 2.44014 12.4608 2.68138 12.49 2.945C12.5192 3.20863 12.4424 3.47305 12.2766 3.68009C12.1109 3.88713 11.8696 4.01983 11.606 4.049C9.57764 4.28135 7.71483 5.28031 6.399 6.84134C5.08316 8.40236 4.41385 10.4074 4.5281 12.4458C4.64236 14.4842 5.53153 16.4018 7.01357 17.806C8.49561 19.2102 10.4584 19.9948 12.5 19.999V21.999C6.977 21.999 2.5 17.523 2.5 11.999C2.5 6.893 6.348 2.621 11.386 2.062V2.061Z"
          fill="url(#paint0_linear_576_11162)"
        />
        <path
          d="M16.822 4.04002C16.9737 3.82259 17.2056 3.67432 17.4667 3.62781C17.7277 3.58131 17.9965 3.64037 18.214 3.79202C19.5379 4.71257 20.6193 5.93997 21.3656 7.36934C22.112 8.7987 22.5012 10.3875 22.5 12C22.5 17.523 18.023 22 12.5 22V20C14.2035 20.0003 15.8626 19.4567 17.2358 18.4485C18.6089 17.4403 19.6244 16.0201 20.1343 14.3947C20.6442 12.7693 20.622 11.0236 20.0708 9.41168C19.5197 7.79978 18.4684 6.40591 17.07 5.43302C16.9622 5.35788 16.8703 5.26223 16.7994 5.15154C16.7286 5.04085 16.6803 4.91729 16.6573 4.78792C16.6343 4.65855 16.637 4.52591 16.6652 4.39758C16.6935 4.26925 16.7468 4.14775 16.822 4.04002Z"
          fill="url(#paint1_linear_576_11162)"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_576_11162"
          x1="7.50002"
          y1="1.99987"
          x2={12}
          y2="20.9999"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset={1} stopColor="white" stopOpacity="0.55" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_576_11162"
          x1="17.5"
          y1={4}
          x2="17.5"
          y2={22}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity={0} />
          <stop offset={1} stopColor="white" stopOpacity="0.55" />
        </linearGradient>
        <clipPath id="clip0_576_11162">
          <rect
            width={24}
            height={24}
            fill="white"
            transform="translate(0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}
